import {
  HabitSchedule,
  ID,
  ScheduleCompletionActions,
  ScheduleEntryState,
  Timestamp,
} from '@griply/types';
import { startOfDay } from 'date-fns';

import { formatDateKey } from '../format-date-key';
import { getScheduleFrequencyDayTarget } from '../get-schedule-frequency-day-target';
import { isBetween } from '../lib/date-fns';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { isDegenerateSchedule } from './is-degenerate-schedule';
import { normalizeSchedules } from './normalize-schedules';

export type ResolvedScheduleDay = {
  /** The matched entry's date when one exists, otherwise the input day. */
  date: Timestamp;
  entryId: ID | null;
  /** `null` when no entry exists for this day (callers map this to their own empty state). */
  state: ScheduleEntryState | null;
  value: number;
  target: number | null;
  hidden: boolean | null;
  onDateManual: boolean;
};

export type GetResolvedScheduleDaysOptions = {
  schedules: HabitSchedule[];
  days: Timestamp[];
  /** rrule occurrences spanning at least `days` (e.g. from `getOccurrences`). */
  occurrences: Timestamp[];
  /** Used as the schedule end when a schedule has no `endDate`. */
  fallbackEndDate: Timestamp;
};

// Later schedules win on overlap; each schedule's effective start is pulled
// back/stitched to the previous schedule's end so there are no gaps.
export const getResolvedScheduleDays = ({
  schedules,
  days,
  occurrences,
  fallbackEndDate,
}: GetResolvedScheduleDaysOptions): ResolvedScheduleDay[] => {
  const resolved = days.map<ResolvedScheduleDay>((date) => ({
    date,
    entryId: null,
    state: null,
    value: 0,
    target: 0,
    hidden: null,
    onDateManual: false,
  }));

  // Normalized here, not at each caller, so no caller can forget it.
  normalizeSchedules(schedules)
    .filter((schedule) => !isDegenerateSchedule(schedule))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .forEach((schedule, index, sortedSchedules) => {
      const prevSchedule = sortedSchedules[index - 1];
      const scheduleStart = getActualScheduleStartDate(schedule);
      const scheduleStartDate = new Date(
        Math.min(
          startOfDay(prevSchedule?.endDate ?? new Date(0)).getTime(),
          scheduleStart.getTime(),
        ),
      );
      const scheduleEndDate = schedule.endDate ?? fallbackEndDate;

      const occurrenceDateKeys = new Set(
        occurrences
          .filter((date) => isBetween(date, scheduleStartDate, scheduleEndDate))
          .map(formatDateKey),
      );
      const dateKeyEntryMap = schedule.entries.reduce<Record<string, number>>(
        (acc, entry, entryIndex) => {
          acc[formatDateKey(entry.date)] = entryIndex;
          return acc;
        },
        {},
      );

      resolved.forEach((day) => {
        if (!isBetween(day.date, scheduleStartDate, scheduleEndDate)) {
          return;
        }

        const dayKey = formatDateKey(day.date);
        const entryOccurrence = occurrenceDateKeys.has(dayKey);
        const scheduleEntry = schedule.entries[dateKeyEntryMap[dayKey]];

        day.date = scheduleEntry?.date ?? day.date;
        day.entryId = scheduleEntry?.id ?? day.entryId;
        day.state = scheduleEntry?.state ?? day.state;
        day.value = scheduleEntry?.value ?? day.value;
        day.hidden = scheduleEntry?.hidden ?? day.hidden;
        day.target = schedule.frequency
          ? getScheduleFrequencyDayTarget(schedule)
          : entryOccurrence
            ? schedule.completionTarget.count
            : day.target;
        day.onDateManual =
          schedule.action?.type ===
          ScheduleCompletionActions.LogCompletionValue;
      });
    });

  return resolved;
};
